if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.select = this.querySelector("select.dropdown-collection");
      this.input = this.querySelector("input.input-collection");
      this.selects = this.querySelectorAll("select.dropdown-filter");
      this.isVehicleFamilyFinder = this.hasAttribute(
        "data-vehicle-family-finder",
      );
      this.isFitmentFinder = this.hasAttribute("data-fitment-finder");
      this.yearSelect = this.findFilterSelect("year");
      this.makeSelect = this.findFilterSelect("make");
      this.modelSelect = this.findFilterSelect("model");
      this.familyMatch = this.querySelector("[data-family-match]");
      this.familyMatchLabel = this.querySelector(
        "[data-family-match-label]",
      );
      this.requestId = 0;
      this.abortController = null;
      this.fitmentCatalog = [];
      this.allowedModelsByMake = {
        "Citroën": ["Jumper", "Jumper / Relay"],
        Fiat: ["Ducato"],
        Ford: ["Transit", "Transit 150 / 250 / 350"],
        Freightliner: ["Sprinter"],
        "Mercedes-Benz": ["Metris", "Sprinter", "V-Class", "Vito"],
        Peugeot: ["Boxer"],
        Ram: ["ProMaster"],
        Universal: ["Universal"],
        Volkswagen: ["Crafter", "ID. Buzz", "Transporter", "Vanagon"],
      };
      this.technicalModelLabelsByMake = {
        "Citroën": {
          Jumper: "Jumper — X250 / X290 platform",
          "Jumper / Relay": "Jumper / Relay — X250 / X290 platform",
        },
        Fiat: { Ducato: "Ducato — X250 / X290 platform" },
        Ford: {
          Transit: "Transit 150 / 250 / 350 — V363",
          "Transit 150 / 250 / 350": "Transit 150 / 250 / 350 — V363",
        },
        Freightliner: {
          Sprinter: "Sprinter — 906 / NCV3 or 907 / VS30",
        },
        "Mercedes-Benz": {
          Metris: "Metris — W447",
          Sprinter: "Sprinter — 906 / NCV3 or 907 / VS30",
          "V-Class": "V-Class — W447",
          Vito: "Vito — W447",
        },
        Peugeot: { Boxer: "Boxer — X250 / X290 platform" },
        Ram: { ProMaster: "ProMaster — X250 / X290 platform" },
        Universal: { Universal: "Universal / multi-vehicle" },
        Volkswagen: {
          Crafter: "Crafter — 2E / 2F (Sprinter 906 platform)",
          "ID. Buzz": "ID. Buzz — MEB platform",
          Transporter: "Transporter — T3 / T25",
          Vanagon: "Vanagon — T3 / T25",
        },
      };

      if (this.isVehicleFamilyFinder) {
        this.setupVehicleFamilyFinder();
      }

      this.setupEventListeners();

      if (this.isFitmentFinder) {
        this.setupFitmentTagFinder();
      // There's no collection dropdown so we either need to populate
      // the filters of the entire store or the single collection chosen
      // (if only one collection has been chosen).
      } else if (!this.select) {
        this.populateCollectionFilters(true);
      }
    }

    findFilterSelect(label) {
      return Array.from(this.selects).find(
        (select) => select.dataset.label?.trim().toLowerCase() === label,
      );
    }

    setupVehicleFamilyFinder() {
      [this.makeSelect, this.modelSelect].forEach((select) => {
        if (!select) {
          return;
        }

        select.disabled = true;
        select.closest(".form-group").hidden = true;
      });
    }

    async setupFitmentTagFinder() {
      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });
      this.button.disabled = true;

      try {
        this.fitmentCatalog = await window.TerrawagenFitment.loadCatalog(
          this.dataset.fitmentCatalogUrl,
        );
        const makeCounts = window.TerrawagenFitment.optionCounts(
          this.fitmentCatalog,
          {},
          "make",
        );
        this.addFitmentOptions(
          this.makeSelect,
          makeCounts,
          window.TerrawagenFitment.makeLabel,
        );
        this.makeSelect.disabled = false;
        this.makeSelect.closest(".form-group").classList.remove("disabled");
      } catch (error) {
        console.error("Unable to load exact vehicle finder data.", error);
      }
    }

    addFitmentOptions(select, counts, labeler) {
      if (!select) return;
      [...counts.entries()]
        .sort(([left], [right]) => {
          if (select === this.yearSelect) {
            if (left === "all") return 1;
            if (right === "all") return -1;
            return Number(left) - Number(right);
          }
          return labeler(left).localeCompare(labeler(right), undefined, {
            numeric: true,
          });
        })
        .forEach(([value, count]) => {
          const option = document.createElement("option");
          option.value = value;
          option.textContent = `${labeler(value)} (${count})`;
          select.append(option);
        });
    }

    setupEventListeners() {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        window.location = this.form.getAttribute("action");
      });
      this.select?.addEventListener("change", this.onCollectionChange.bind(this));
      this.selects.forEach((event) => {
        event.addEventListener("change", this.onFilterChange.bind(this));
      });
    }

    constructURL() {
      const collectionUrl =
        this.select?.value ?? this.input?.value ?? window.routes.all_products_url;
      const url = new URL(collectionUrl, window.requestShopUrl);

      if (this.isFitmentFinder) {
        const selection = {
          make: this.makeSelect?.value || "",
          model: this.modelSelect?.value || "",
          year: this.yearSelect?.value || "",
        };
        const tag = window.TerrawagenFitment.exactTag(selection);
        if (tag) url.pathname = `${url.pathname.replace(/\/$/, "")}/${tag}`;
        return window.TerrawagenFitment.addPreviewParams(url).toString();
      }

      // Keep Shopify's unpublished-theme preview context on background filter
      // requests and on the final collection URL. Without these parameters the
      // request falls back to the published theme, where this theme's private
      // `do_not_use` collection view may not exist yet.
      const currentUrl = new URL(window.location.href);
      ["preview_theme_id", "pb", "_fd"].forEach((parameter) => {
        if (currentUrl.searchParams.has(parameter)) {
          url.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
        }
      });

      // Shopify can remove the preview query string after the first page load.
      // Recover it from the storefront's current-theme object so collection
      // requests still render against this unpublished theme.
      const currentTheme =
        window.terrawagenThemePreview || window.Shopify?.theme;
      if (
        !url.searchParams.has("preview_theme_id") &&
        currentTheme?.id &&
        currentTheme?.role !== "main"
      ) {
        url.searchParams.set("preview_theme_id", currentTheme.id);
        url.searchParams.set("_fd", "0");
        url.searchParams.set("pb", "0");
      }

      this.querySelectorAll("select.dropdown-filter:enabled").forEach((select) => {
        if (select.value === "") {
          return;
        }

        Array.from(select.selectedOptions).forEach((option) => {
          const parameter = option.dataset.paramName;
          const rawValues = JSON.parse(option.dataset.rawValues || "[]");
          url.searchParams.delete(parameter);

          if (rawValues.length > 0) {
            rawValues.forEach((rawValue) => url.searchParams.append(parameter, rawValue));
            const key = select.dataset.label?.trim().toLowerCase();
            if (this.isFitmentFinder && ["make", "model", "year"].includes(key)) {
              url.searchParams.set(`tw_${key}`, option.value);
            }
          } else {
            url.searchParams.set(parameter, option.value);
          }
        });
      });

      return url.toString();
    }

    setFormUrl(url, background) {
      this.form.setAttribute("action", url);

      // This operation happens on the background
      // where the user should not be notified of loading with spinners
      // etc.
      if (!background) {
        this.button.disabled = false;
      }
    }

    selectRemoveOptions(select) {
      const options = select.querySelectorAll("option:not(.default)");

      options.forEach((option) => {
        select.removeChild(option);
      });

      select.querySelector("option").selected = true;
    }

    onFilterChange(event) {
      if (this.isFitmentFinder) {
        this.onFitmentTagChange(event.target);
        return;
      }

      if (
        this.isVehicleFamilyFinder &&
        event.target === this.yearSelect
      ) {
        this.setFormUrl(this.constructURL(), true);
        this.button.disabled = this.yearSelect.value === "";
        return;
      }

      const selectedIndex = event.target.dataset.index;

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= Number(selectedIndex)) {
          return;
        }

        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.setFormUrl(this.constructURL());

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");
      const request = this.startRequest();
      this.setLoading(true);

      fetch(url, { signal: request.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          if (request.id !== this.requestId) {
            return;
          }

          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          this.selects.forEach((select) => {
            if (Number(select.dataset.index) <= Number(selectedIndex)) {
              return;
            }

            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );
            if (options.length > 0) {
              const availableOptions = this.formatFitmentOptions(
                select,
                Array.from(options),
              ).filter(
                (option) =>
                  Number(option.dataset.count) > 0 &&
                  this.isFitmentOptionAllowed(select, option),
              );
              select.append(...availableOptions);
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === Number(selectedIndex) + 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Unable to update vehicle finder options.", error);
          }
        })
        .finally(() => {
          if (request.id === this.requestId) {
            this.setLoading(false);
          }
        });
    }

    onFitmentTagChange(select) {
      if (select === this.makeSelect) {
        this.selectRemoveOptions(this.modelSelect);
        this.selectRemoveOptions(this.yearSelect);
        this.yearSelect.disabled = true;
        this.yearSelect.closest(".form-group").classList.add("disabled");

        const modelCounts = window.TerrawagenFitment.optionCounts(
          this.fitmentCatalog,
          { make: this.makeSelect.value },
          "model",
        );
        this.addFitmentOptions(
          this.modelSelect,
          modelCounts,
          window.TerrawagenFitment.modelLabel,
        );
        this.modelSelect.disabled = false;
        this.modelSelect.closest(".form-group").classList.remove("disabled");
      } else if (select === this.modelSelect) {
        this.selectRemoveOptions(this.yearSelect);
        const yearCounts = window.TerrawagenFitment.optionCounts(
          this.fitmentCatalog,
          { make: this.makeSelect.value, model: this.modelSelect.value },
          "year",
        );
        this.addFitmentOptions(
          this.yearSelect,
          yearCounts,
          window.TerrawagenFitment.yearLabel,
        );
        this.yearSelect.disabled = false;
        this.yearSelect.closest(".form-group").classList.remove("disabled");
      }

      this.button.disabled = !this.yearSelect?.value;
      this.setFormUrl(this.constructURL(), true);
    }

    populateCollectionFilters(background = false) {
      this.setFormUrl(this.constructURL(), background);

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");
      const request = this.startRequest();

      // When we initially populate the filters we don't want
      // to show a loading spinner.
      if (!background) {
        this.setLoading(true);
      }

      fetch(url, { signal: request.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          if (request.id !== this.requestId) {
            return;
          }

          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          this.selects.forEach((select) => {
            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );
            if (options.length > 0) {
              select.append(...this.formatFitmentOptions(select, Array.from(options)));
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Unable to load vehicle finder options.", error);
          }
        })
        .finally(() => {
          if (!background && request.id === this.requestId) {
            this.setLoading(false);
          }
        });
    }

    onCollectionChange() {
      if (this.isVehicleFamilyFinder && this.populateVehicleFamilyYears()) {
        return;
      }

      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.populateCollectionFilters();
    }

    isFitmentOptionAllowed(select, option) {
      if (!this.isFitmentFinder || select !== this.modelSelect) {
        return true;
      }

      const selectedMake = this.makeSelect?.value;
      const allowedModels = this.allowedModelsByMake[selectedMake];
      return !allowedModels || allowedModels.includes(option.value);
    }

    formatFitmentOptions(select, options) {
      if (!this.isFitmentFinder) {
        return options;
      }

      const groupedOptions = new Map();
      options.forEach((sourceOption) => {
        const rawValue = sourceOption.value.trim();
        const tokens = rawValue
          .split(";")
          .map((part) => part.trim())
          .filter(Boolean);

        tokens.forEach((token) => {
          if (!groupedOptions.has(token)) {
            groupedOptions.set(token, {
              rawValues: new Set(),
              count: 0,
              paramName: sourceOption.dataset.paramName,
            });
          }

          const group = groupedOptions.get(token);
          group.rawValues.add(rawValue);
          group.count += Number(sourceOption.dataset.count) || 0;
        });
      });

      const expandedOptions = Array.from(groupedOptions.entries())
        .sort(([left], [right]) => {
          if (select === this.yearSelect) return Number(left) - Number(right);
          return left.localeCompare(right, undefined, { numeric: true });
        })
        .map(([token, group]) => {
          const option = document.createElement("option");
          option.value = token;
          option.textContent = `${token} (${group.count})`;
          option.dataset.count = group.count;
          option.dataset.paramName = group.paramName;
          option.dataset.rawValues = JSON.stringify(Array.from(group.rawValues));
          return option;
        });

      if (select !== this.modelSelect) {
        return expandedOptions;
      }

      const selectedMake = this.makeSelect?.value;
      const labelsForMake = this.technicalModelLabelsByMake[selectedMake];
      if (!labelsForMake) {
        return expandedOptions;
      }

      expandedOptions.forEach((option) => {
        const technicalLabel = labelsForMake[option.value];
        if (!technicalLabel) {
          return;
        }

        const count = option.dataset.count;
        option.textContent = `${technicalLabel}${count ? ` (${count})` : ""}`;
        option.title = `Catalog model: ${option.value}`;
      });

      return expandedOptions;
    }

    populateVehicleFamilyYears() {
      const selectedOption = this.select?.selectedOptions?.[0];
      const familyName = selectedOption?.dataset.familyName;
      const validYears = selectedOption?.dataset.validYears;

      if (!familyName || !validYears || !this.yearSelect) {
        return false;
      }

      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      validYears.split(",").forEach((year) => {
        const option = document.createElement("option");
        option.value = year;
        option.textContent = year;
        option.dataset.paramName = "filter.p.m.custom.year";
        this.yearSelect.append(option);
      });

      this.yearSelect.disabled = false;
      this.yearSelect.closest(".form-group").classList.remove("disabled");

      if (this.familyMatch && this.familyMatchLabel) {
        this.familyMatchLabel.textContent = familyName;
        this.familyMatch.hidden = false;
      }

      this.setFormUrl(this.constructURL(), true);
      this.button.disabled = true;

      return true;
    }

    startRequest() {
      this.requestId += 1;
      this.abortController?.abort();
      this.abortController = new AbortController();

      return {
        id: this.requestId,
        signal: this.abortController.signal,
      };
    }

    setLoading(loading) {
      this.classList.toggle("is-loading", loading);
      this.button.classList.toggle("loading", loading);
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}
